import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, RefreshCw, Search } from 'lucide-react';
import { supabase } from '../../services/supabase';

type SubscriberRow = {
  user_id: string;
  email: string | null;
  full_name: string | null;
  plan: string | null;
  plan_id: string | null;
  price_id: string | null;
  pricing_version: string | null;
  billing_currency: string | null;
  stripe_customer_id: string | null;
  credits: number | null;
  updated_at: string | null;
  last_grant_at: string | null;
};

type GrantRow = {
  id: string;
  created_at: string | null;
  user_id: string;
  email: string | null;
  credits: number | null;
  type: string | null;
  credit_type: string | null;
  description: string | null;
  stripe_session_id: string | null;
  currency: string | null;
  app: string | null;
  pricing_version: string | null;
  amount_cents: number | null;
  charge_currency: string | null;
  billing_type: string | null;
  billing_status: string | null;
};

type WorkspaceRow = {
  team_id: string;
  team_name: string | null;
  owner_email: string | null;
  status: string;
  plan_type: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  created_at: string | null;
  updated_at: string | null;
};

function formatWhen(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatMoney(cents: number | null, currency: string | null): string {
  if (cents == null) return '—';
  const code = (currency || 'gbp').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${code}`;
  }
}

function isRecent(value: string | null): boolean {
  if (!value) return false;
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then < 48 * 60 * 60 * 1000;
}

export default function SubscriptionsTab({ onOpenUser }: { onOpenUser?: (userId: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [activeCount, setActiveCount] = useState(0);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [subscribers, setSubscribers] = useState<SubscriberRow[]>([]);
  const [events, setEvents] = useState<GrantRow[]>([]);
  const [workspaces, setWorkspaces] = useState<WorkspaceRow[]>([]);

  const load = async (query = appliedSearch) => {
    setLoading(true);
    setError(null);
    try {
      if (!supabase) throw new Error('Supabase not configured');
      const { data, error: rpcError } = await supabase.rpc('admin_list_rehub_subscriptions', {
        p_search: query.trim() || null,
        p_limit: 100,
      });
      if (rpcError) throw rpcError;
      const payload = data as {
        success?: boolean;
        error?: string;
        active_count?: number;
        events_total?: number;
        subscribers?: SubscriberRow[];
        events?: GrantRow[];
        workspaces?: WorkspaceRow[];
      } | null;
      if (!payload?.success) throw new Error(payload?.error || 'Failed to load subscriptions');
      setActiveCount(payload.active_count || 0);
      setEventsTotal(payload.events_total || 0);
      setSubscribers(payload.subscribers || []);
      setEvents(payload.events || []);
      setWorkspaces(payload.workspaces || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load('');
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <CreditCard size={18} className="text-teal-400" />
            ReHub subscriptions
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Monthly credit plans and the grant that records each payment. One-off packs stay on Sales.
          </p>
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedSearch(search);
            void load(search);
          }}
        >
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Email or plan"
              className="pl-8 pr-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm w-52"
            />
          </div>
          <button
            type="button"
            onClick={() => void load(appliedSearch)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </form>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-xs text-slate-500">Active plans</p>
          <p className="text-2xl font-bold text-teal-300 tabular-nums">{loading ? '—' : activeCount}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-4">
          <p className="text-xs text-slate-500">Subscription grants</p>
          <p className="text-2xl font-bold text-white tabular-nums">{loading ? '—' : eventsTotal}</p>
        </div>
      </div>

      <section className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800">
          <h3 className="text-sm font-semibold text-white">Current plans</h3>
          <p className="text-xs text-slate-500">Profiles with a ReHub credit subscription set.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-slate-500 text-xs">
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-4 py-2 text-left">Currency</th>
                <th className="px-4 py-2 text-right">Credits</th>
                <th className="px-4 py-2 text-left">Last grant</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500"><Loader2 size={18} className="animate-spin mx-auto" /></td></tr>
              ) : subscribers.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No active ReHub credit plans</td></tr>
              ) : subscribers.map((row) => (
                <tr key={row.user_id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="px-4 py-2.5">
                    {onOpenUser ? (
                      <button
                        type="button"
                        onClick={() => onOpenUser(row.user_id)}
                        className="text-left text-slate-200 text-xs hover:text-teal-300 underline-offset-2 hover:underline"
                      >
                        {row.email || '—'}
                      </button>
                    ) : (
                      <div className="text-slate-200 text-xs">{row.email || '—'}</div>
                    )}
                    {row.full_name && <div className="text-slate-500 text-[11px]">{row.full_name}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-white text-xs font-medium">{row.plan || row.plan_id || '—'}</div>
                    <div className="text-slate-500 text-[11px]">{row.plan_id || row.price_id || ''}</div>
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{row.billing_currency || '—'}{row.pricing_version ? ` · ${row.pricing_version}` : ''}</td>
                  <td className="px-4 py-2.5 text-right text-slate-300 text-xs tabular-nums">{row.credits ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs">
                    <span className={isRecent(row.last_grant_at) ? 'text-orange-300' : 'text-slate-400'}>
                      {formatWhen(row.last_grant_at)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-800">
          <h3 className="text-sm font-semibold text-white">Subscription records</h3>
          <p className="text-xs text-slate-500">
            Credit grants from new subscriptions and renewals. Highlighted rows are from the last 48 hours.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-slate-500 text-xs">
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">Customer</th>
                <th className="px-4 py-2 text-right">Credits</th>
                <th className="px-4 py-2 text-right">Paid</th>
                <th className="px-4 py-2 text-left">Record</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500"><Loader2 size={18} className="animate-spin mx-auto" /></td></tr>
              ) : events.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No subscription grants on file</td></tr>
              ) : events.map((row) => (
                <tr key={row.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${isRecent(row.created_at) ? 'text-orange-300' : 'text-slate-400'}`}>
                    {formatWhen(row.created_at)}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {onOpenUser ? (
                      <button
                        type="button"
                        onClick={() => onOpenUser(row.user_id)}
                        className="text-slate-200 hover:text-teal-300 underline-offset-2 hover:underline"
                      >
                        {row.email || '—'}
                      </button>
                    ) : (
                      <span className="text-slate-200">{row.email || '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right text-white text-xs tabular-nums">{row.credits ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right text-slate-300 text-xs tabular-nums">
                    {formatMoney(row.amount_cents, row.charge_currency || row.currency)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-slate-300 text-xs">{row.description || row.type || '—'}</div>
                    <div className="text-slate-500 text-[11px] font-mono truncate max-w-[220px]">{row.stripe_session_id || ''}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {workspaces.length > 0 && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/50 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800">
            <h3 className="text-sm font-semibold text-white">Team workspaces</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-slate-500 text-xs">
                  <th className="px-4 py-2 text-left">Team</th>
                  <th className="px-4 py-2 text-left">Owner</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Renews</th>
                </tr>
              </thead>
              <tbody>
                {workspaces.map((row) => (
                  <tr key={row.team_id} className="border-b border-slate-800/50">
                    <td className="px-4 py-2.5 text-slate-200 text-xs">{row.team_name || '—'}</td>
                    <td className="px-4 py-2.5 text-slate-400 text-xs">{row.owner_email || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-orange-300">{row.status}</td>
                    <td className="px-4 py-2.5 text-slate-400 text-xs">{formatWhen(row.current_period_end)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
